import { vec3 } from 'gl-matrix'
import Node from 'nanogl-node'

import gui from '@/webgl/dev/gui'
import { clamp01, map, mix, randomFloat } from '@/webgl/math'
import { ScrollItem, ScrollSide } from '@/webgl/runner/entities/BackgroundEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { ISystem } from '@/webgl/runner/interfaces/ISystem'

export default class BackgroundSystem implements ISystem {
  spawnSide: ScrollSide = 1
  node: Node = new Node()

  _spawnableCache: string[] = []

  constructor(readonly world: GameWorld) {
    this.world.root.add(this.node)

    /// #if _DEBUG
    const fld = gui.folder('Background')
    fld.add(this.world.background, 'minFrequency', { min: 0, max: 5 })
    fld.add(this.world.background, 'maxFrequency', { min: 0, max: 5 })
    /// #endif
  }

  spawnItem(): ScrollItem {
    const settings = this.world.settings
    const background = this.world.background
    const assets = this.world.registry

    const spawnableItems = background.spawnableItems
    const activeIds = background.activeItems.map((i) => i.id)
    const filteredIds = spawnableItems.filter((id) => !activeIds.includes(id))

    const scrollItem = assets.scrolls.getItemForSide(this.spawnSide, filteredIds)
    if (scrollItem == null) return null

    const bounds = scrollItem.bounds
    const scale = scrollItem.scale || 1

    let x = this.spawnSide * (settings.LANE_SIZE + settings.LANE_SIZE * 0.35)
    if (this.spawnSide == ScrollSide.LEFT) x -= bounds.max[0] * scale
    if (this.spawnSide == ScrollSide.RIGHT) x -= bounds.min[0] * scale

    const { id } = scrollItem

    const side = this.spawnSide
    const node = new Node()
    const lifetime = 0
    const position = vec3.fromValues(x, 0, -settings.LANE_LENGTH * 0.9)
    const revealDuration = 0.35

    node.position.set(position)
    node.setScale(scale)
    node.invalidate()

    const item: ScrollItem = {
      node,
      id,
      lifetime,
      side,
      position,
      scale,
      revealDuration,
    }

    this.node.add(node)
    background.activeItems.push(item)

    this.spawnSide = this.spawnSide === ScrollSide.LEFT ? ScrollSide.RIGHT : ScrollSide.LEFT
    background.nextSpawn = randomFloat(background.minFrequency, background.maxFrequency)
    background.spawnTime = 0
    return item
  }

  checkSpawnables() {
    const background = this.world.background
    if (this._spawnableCache.length == 0) {
      this._spawnableCache.push(...background.spawnableItems)
    }

    // Test if _spawnableCache values are the same as background.spawnableItems
    const spawnableUpdated: boolean =
      this._spawnableCache.length !== background.spawnableItems.length ||
      this._spawnableCache.some((v, i) => v !== background.spawnableItems[i])
    if (spawnableUpdated) {
      for (let i = background.activeItems.length - 1; i >= 0; i--) {
        this.destroyItem(background.activeItems[i])
      }
      this._spawnableCache = [...background.spawnableItems]
      background.spawnTime = 0
      background.nextSpawn = 0
    }
  }

  start(): void {
    const max = 2
    const background = this.world.background

    for (let i = 0; i < max; i++) {
      const item = this.spawnItem()
      if (item == null) continue
      item.node.z += 1.0 + Math.random() * 1.0
      background.spawnTime = 0
    }
  }

  update(time: TimeEntity): void {
    const settings = this.world.settings
    const background = this.world.background

    // this.checkSpawnables();

    background.spawnTime += time.deltaTime * settings.scrollSpeed
    if (background.spawnTime > background.nextSpawn) {
      this.spawnItem()
      background.spawnTime = 0
    }

    const destroyable = []
    const dt = mix(time.unscaledDeltaTime, time.deltaTime, this.world.state.value.jauge)

    for (const entity of background.activeItems) {
      entity.node.z += dt * settings.scrollSpeed * 1.0
      entity.lifetime += time.deltaTime

      const reveal = clamp01(map(entity.lifetime, 0, entity.revealDuration, 0, 1))
      entity.node.setScale(reveal * entity.scale)

      const phase = Math.abs(entity.node.z) / (settings.LANE_LENGTH - 2)
      const offset = (1.0 - Math.sin(phase * Math.PI)) * 0.4
      entity.node.x = entity.position[0] + offset * entity.side

      if (entity.node.z > -2) {
        destroyable.push(entity)
      }
    }

    background.activeItems.sort((a, b) => a.node.z - b.node.z)

    for (const entity of destroyable) {
      this.destroyItem(entity)
    }
  }

  destroyItem(item: ScrollItem) {
    const idx = this.world.background.activeItems.indexOf(item)
    item.node._parent.remove(item.node)
    this.world.background.activeItems.splice(idx, 1)
  }

  dispose(): void {
    this.world.root.remove(this.node)
  }
}
